import React, { useCallback, type SyntheticEvent, useMemo } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import { SpotlightTarget } from '@atlaskit/onboarding';
import Tooltip from '@atlaskit/tooltip';
import {
	ActionsWrapper,
	LeftActionsWrapper,
	RightActionsWrapper,
} from '@atlassian/jira-business-app-wrapper';
import { isJwmTemplateBundlesEnabled } from '@atlassian/jira-business-feature-flags';
import { Filters, SimpleSearchField, useHideDoneItems } from '@atlassian/jira-business-filters';
import {
	JWMShareButton,
	integrationTypes,
	AddPeopleWithAvatar,
} from '@atlassian/jira-business-invite-people';
import {
	CALENDAR_EMBED_NUDGE_STEP,
	useNudgeStep,
} from '@atlassian/jira-business-onboarding-calendar-embed';
import { ThemedButton } from '@atlassian/jira-business-theme-components';
import { useIntl } from '@atlassian/jira-intl';
import type { DateOnly } from '@atlassian/jira-platform-utils-date-only';
import { useStartDateField } from '@atlassian/jira-router-resources-business-configuration';
import { ConditionalNudgeWrapper } from '@atlassian/jira-software-onboarding-nudges--next';
import {
	NavigateCalendarNudgeAsync,
	CalendarReleasesNudgeAsync,
} from '@atlassian/jira-software-onboarding-nudges--next/src/async';
import UnscheduledIcon from '../../../common/ui/unscheduled-icon';
import { useCalendarMoreMenuStore } from '../../../controllers/calendar-more-menu-store';
import { useCalendarFeatures } from '../../../controllers/features-context';
import { useUnscheduledItemsCount } from '../../../controllers/unscheduled-items-count';
import messages from './messages';
import MoreMenu from './more-menu';

export type ToolbarProps = {
	isDisabled: boolean;
	isUnscheduledSidebarOpen?: boolean;
	currentViewStartDateOnly: DateOnly;
	onTodayClick: (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;
	onNextClick: (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;
	onPrevClick: (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;
	onUnscheduledClick?: (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

export const Toolbar = ({
	onTodayClick,
	onNextClick,
	onPrevClick,
	onUnscheduledClick,
	currentViewStartDateOnly,
	isDisabled,
	isUnscheduledSidebarOpen,
}: ToolbarProps) => {
	const { formatMessage, formatDate } = useIntl();
	const [hideDoneItems, setHideDoneItems] = useHideDoneItems();
	const [{ nudgeStep, shouldOpenModal }, { setShouldOpenModal }] = useNudgeStep();

	const {
		inviteButtonInteraction,
		hasInviteButton,
		hasPresence,
		hasShareButton,
		hasUnscheduledButton,
		hasFilterCustomFields,
		hasSoftwareReleases,
	} = useCalendarFeatures();
	const count = useUnscheduledItemsCount();

	const { loading: startDateFieldLoading } = useStartDateField();

	const handleDialogClose = useCallback(() => {
		if (shouldOpenModal) {
			setShouldOpenModal(false);
		}
	}, [setShouldOpenModal, shouldOpenModal]);

	const showShareButtonSpotlight = useMemo(
		() => nudgeStep === CALENDAR_EMBED_NUDGE_STEP,
		[nudgeStep],
	);

	const shareButton = (
		<JWMShareButton
			shareTitle={messages.shareTitle}
			integrationType={integrationTypes.CALENDAR}
			shareContentType="calendar"
			isAutoOpenDialog={shouldOpenModal}
			onDialogClose={handleDialogClose}
		/>
	);

	const wrappedShareButton = showShareButtonSpotlight ? (
		<SpotlightTarget name={CALENDAR_EMBED_NUDGE_STEP}>
			<div>{shareButton}</div>
		</SpotlightTarget>
	) : (
		shareButton
	);

	const searchBar = <SimpleSearchField placeholder={messages.simpleSearch} view="calendar" />;

	const addPeopleWithPresence = (
		<AddPeopleWithAvatar
			view="calendar"
			isPresenceEnabledInContext={hasPresence}
			isOpenNewTabOnClick={inviteButtonInteraction === 'openNewTab'}
			hasInviteButton={hasInviteButton}
		/>
	);

	const currentMonth = (
		<CurrentMonthWrapper>
			{formatDate(currentViewStartDateOnly.toLocalDate(), {
				year: 'numeric',
				month: 'long',
			})}
		</CurrentMonthWrapper>
	);

	const previousMonthButton = (
		<Tooltip content={formatMessage(messages.prevMonthButton)}>
			<ThemedButton
				iconBefore={<ChevronLeftLargeIcon label={formatMessage(messages.prevMonthButton)} />}
				isLoading={isDisabled}
				appearance="subtle"
				onClick={onPrevClick}
				testId="business-calendar.ui.calendar-view.toolbar.prev"
			/>
		</Tooltip>
	);

	const nextMonthButton = (
		<Tooltip content={formatMessage(messages.nextMonthButton)}>
			<ThemedButton
				iconBefore={<ChevronRightLargeIcon label={formatMessage(messages.nextMonthButton)} />}
				isLoading={isDisabled}
				appearance="subtle"
				onClick={onNextClick}
				testId="business-calendar.ui.calendar-view.toolbar.next"
			/>
		</Tooltip>
	);

	const todayButton = (
		<Tooltip
			content={formatDate(Date.now(), {
				weekday: 'long',
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})}
		>
			<ThemedButton
				isDisabled={isDisabled}
				onClick={onTodayClick}
				testId="business-calendar.ui.calendar-view.toolbar.today"
			>
				{formatMessage(messages.todayButton)}
			</ThemedButton>
		</Tooltip>
	);

	const unscheduledButton = hasUnscheduledButton && (
		<ThemedButton
			isDisabled={startDateFieldLoading}
			onClick={onUnscheduledClick}
			isSelected={isUnscheduledSidebarOpen}
			aria-expanded={isUnscheduledSidebarOpen}
			iconBefore={<UnscheduledIcon size="small" label={formatMessage(messages.unscheduled)} />}
			appearance="subtle"
			testId="business-calendar.ui.calendar-view.toolbar.unscheduled"
		>
			{count
				? `${formatMessage(messages.unscheduled)} (${count > 999 ? '999+' : count})`
				: `${formatMessage(messages.unscheduled)}`}
		</ThemedButton>
	);

	const [moreMenuState, moreMenuActions] = useCalendarMoreMenuStore();
	const onShow = useCallback(() => moreMenuActions.open('calendar-more-button'), [moreMenuActions]);

	const shareActionButton = hasShareButton && wrappedShareButton;

	const filters = <Filters view="calendar" allowCustomFields={hasFilterCustomFields} />;

	const wrappedMoreMenu = (
		<MoreMenu
			hideDoneItems={Boolean(hideDoneItems)}
			onChangeHideDoneItems={setHideDoneItems}
			forceVisibility={moreMenuState.isOpen}
		/>
	);

	const moreMenu = isJwmTemplateBundlesEnabled() ? (
		<SpotlightTarget name="calendarMoreMenuSpotlight">
			<ConditionalNudgeWrapper
				conditionsToApplyWrapper={hasSoftwareReleases}
				Wrapper={CalendarReleasesNudgeAsync}
				onShow={onShow}
			>
				{wrappedMoreMenu}
			</ConditionalNudgeWrapper>
		</SpotlightTarget>
	) : (
		<ConditionalNudgeWrapper
			conditionsToApplyWrapper={hasSoftwareReleases}
			Wrapper={CalendarReleasesNudgeAsync}
			onShow={onShow}
		>
			{wrappedMoreMenu}
		</ConditionalNudgeWrapper>
	);

	return (
		<NavigateCalendarNudgeAsync>
			<ActionsWrapper>
				<LeftActionsWrapper>
					{searchBar}
					{addPeopleWithPresence}
				</LeftActionsWrapper>

				<RightActionsWrapper>
					{currentMonth}
					{previousMonthButton}
					{nextMonthButton}
					{todayButton}
					{shareActionButton}
					{unscheduledButton}
					{filters}
					{moreMenu}
				</RightActionsWrapper>
			</ActionsWrapper>
		</NavigateCalendarNudgeAsync>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CurrentMonthWrapper = styled.div({
	fontWeight: 700,
});
